export interface ParsedLogTrace {
  requestId?: string
  upstreamRequestId?: string
  requestAt?: number
  requestPath?: string
  method?: string
}

export interface ParsedLogTiming {
  requestReadMs?: number
  authMs?: number
  routeSelectMs?: number
  preBillingMs?: number
  upstreamConnectMs?: number
  upstreamTtfbMs?: number
  upstreamTotalMs?: number
  transformMs?: number
  clientWriteMs?: number
  settleBillingMs?: number
  totalMs?: number
}

export interface ParsedLogStream {
  enabled?: boolean
  status?: string
  clientTtftMs?: number
  firstChunkRelayMs?: number
  chunks?: number
  finishReason?: string
  clientAbort?: boolean
  clientDisconnected?: boolean
  upstreamInterrupted?: boolean
  scannerError?: boolean
  contextCancelled?: boolean
  usageMissing?: boolean
}

export interface ParsedLogRoute {
  requestedModel?: string
  upstreamModel?: string
  channelId?: number
  channelName?: string
  channelType?: number
  channelTag?: string
  retryCount?: number
  usingGroup?: string
  autoGroup?: string
}

export interface ParsedLogBilling {
  preConsumedQuota?: number
  actualQuota?: number
  refundQuota?: number
  modelRatio?: number
  groupRatio?: number
  completionRatio?: number
  cacheRatio?: number
  billingSource?: string
  /** 管理员日志 other 顶层字段：用户扣费 USD（micro） */
  userCostMicroUsd?: number
  /** 管理员日志 other 顶层字段：渠道成本 USD（micro） */
  channelCostMicroUsd?: number
  /** model_table | default_ratio */
  channelCostSource?: string
}

export interface ParsedLogError {
  stage?: string
  code?: string
  message?: string
  upstreamStatus?: number
}

export interface ParsedLogOther {
  trace?: ParsedLogTrace
  timing?: ParsedLogTiming
  stream?: ParsedLogStream
  route?: ParsedLogRoute
  billing?: ParsedLogBilling
  error?: ParsedLogError
  raw?: Record<string, unknown>
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>
  return null
}

function num(v: unknown): number | undefined {
  if (v == null || v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

function str(v: unknown): string | undefined {
  if (v == null) return undefined
  const s = String(v).trim()
  return s || undefined
}

function bool(v: unknown): boolean | undefined {
  if (v == null) return undefined
  if (typeof v === 'boolean') return v
  if (v === 'true' || v === 1 || v === '1') return true
  if (v === 'false' || v === 0 || v === '0') return false
  return undefined
}

function parseNested<T>(obj: Record<string, unknown> | null, key: string, mapper: (r: Record<string, unknown>) => T): T | undefined {
  const nested = asRecord(obj?.[key])
  if (!nested) return undefined
  return mapper(nested)
}

export function parseLogOther(raw?: string | Record<string, unknown> | null): ParsedLogOther | null {
  let obj: Record<string, unknown> | null = null
  if (!raw) return null
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return null
    try {
      obj = asRecord(JSON.parse(trimmed))
    } catch {
      return null
    }
  } else {
    obj = asRecord(raw)
  }
  if (!obj) return null

  return {
    trace: parseNested(obj, 'trace', (r) => ({
      requestId: str(r.request_id ?? r.requestId),
      upstreamRequestId: str(r.upstream_request_id ?? r.upstreamRequestId),
      requestAt: num(r.request_at ?? r.requestAt),
      requestPath: str(r.request_path ?? r.requestPath),
      method: str(r.method),
    })),
    timing: parseNested(obj, 'timing', (r) => ({
      requestReadMs: num(r.request_read_ms),
      authMs: num(r.auth_ms),
      routeSelectMs: num(r.route_select_ms),
      preBillingMs: num(r.pre_billing_ms),
      upstreamConnectMs: num(r.upstream_connect_ms),
      upstreamTtfbMs: num(r.upstream_ttfb_ms),
      upstreamTotalMs: num(r.upstream_total_ms),
      transformMs: num(r.transform_ms),
      clientWriteMs: num(r.client_write_ms),
      settleBillingMs: num(r.settle_billing_ms),
      totalMs: num(r.total_ms),
    })),
    stream: parseNested(obj, 'stream', (r) => ({
      enabled: bool(r.enabled),
      status: str(r.status),
      clientTtftMs: num(r.client_ttft_ms),
      firstChunkRelayMs: num(r.first_chunk_relay_ms),
      chunks: num(r.chunks),
      finishReason: str(r.finish_reason),
      clientAbort: bool(r.client_abort),
      clientDisconnected: bool(r.client_disconnected),
      upstreamInterrupted: bool(r.upstream_interrupted),
      scannerError: bool(r.scanner_error),
      contextCancelled: bool(r.context_cancelled),
      usageMissing: bool(r.usage_missing),
    })),
    route: parseNested(obj, 'route', (r) => ({
      requestedModel: str(r.requested_model),
      upstreamModel: str(r.upstream_model),
      channelId: num(r.channel_id),
      channelName: str(r.channel_name),
      channelType: num(r.channel_type),
      channelTag: str(r.channel_tag),
      retryCount: num(r.retry_count),
      usingGroup: str(r.using_group),
      autoGroup: str(r.auto_group),
    })),
    billing: (() => {
      const nested = parseNested(obj, 'billing', (r) => ({
        preConsumedQuota: num(r.pre_consumed_quota),
        actualQuota: num(r.actual_quota),
        refundQuota: num(r.refund_quota),
        modelRatio: num(r.model_ratio),
        groupRatio: num(r.group_ratio),
        completionRatio: num(r.completion_ratio),
        cacheRatio: num(r.cache_ratio),
        billingSource: str(r.billing_source),
      }))
      const topLevel = {
        userCostMicroUsd: num(obj.user_cost_micro_usd ?? obj.userCostMicroUsd),
        channelCostMicroUsd: num(obj.channel_cost_micro_usd ?? obj.channelCostMicroUsd),
        channelCostSource: str(obj.channel_cost_source ?? obj.channelCostSource),
      }
      if (!nested && !topLevel.userCostMicroUsd && !topLevel.channelCostMicroUsd && !topLevel.channelCostSource) {
        return undefined
      }
      return { ...nested, ...topLevel }
    })(),
    error: parseNested(obj, 'error', (r) => ({
      stage: str(r.stage),
      code: str(r.code),
      message: str(r.message),
      upstreamStatus: num(r.upstream_status),
    })),
    raw: obj,
  }
}
